import React, { useMemo, useState, useEffect } from "react";
import { Modal, ModalBody, Button, FormGroup, Label } from "reactstrap";
import Select from "react-select";
import { customStyles } from "pages/app-views/indicadores/Form";
import { SignOut } from "@phosphor-icons/react";

const CopyPerfilModal = ({
	isOpen,
	toggle,
	users,
	allUserPermissions,
	onSaveCopy,
}) => {
	const [sourceUserId, setSourceUserId] = useState(null);
	const [targetUserId, setTargetUserId] = useState(null);
	const [isSaving, setIsSaving] = useState(false);

	useEffect(() => {
		if (!isOpen) {
			setSourceUserId(null);
			setTargetUserId(null);
			setIsSaving(false);
		}
	}, [isOpen]);

	const userOptions = useMemo(
		() =>
			(users || []).map((user) => ({
				value: user.id,
				label: `${user.name || ""} ${user.lastname || ""}`.trim(),
			})),
		[users],
	);

	const sourcePermissions = useMemo(
		() =>
			(allUserPermissions || []).filter(
				(role) => role?.user?.id === sourceUserId,
			),
		[allUserPermissions, sourceUserId],
	);

	const targetPermissions = useMemo(
		() =>
			(allUserPermissions || []).filter(
				(role) => role?.user?.id === targetUserId,
			),
		[allUserPermissions, targetUserId],
	);

	const profilesToAdd = useMemo(() => {
		const existingCompanyIds = new Set(
			targetPermissions.map((role) => role.company_id),
		);

		return sourcePermissions.filter(
			(role) => !existingCompanyIds.has(role.company_id),
		);
	}, [sourcePermissions, targetPermissions]);

	const canSave =
		sourceUserId &&
		targetUserId &&
		sourceUserId !== targetUserId &&
		profilesToAdd.length > 0;

	const handleSave = async () => {
		if (!canSave) return;
		setIsSaving(true);
		try {
			await onSaveCopy({
				targetUserId,
				targetPermissions,
				profilesToAdd,
			});
			toggle();
		} finally {
			setIsSaving(false);
		}
	};

	return (
		<Modal isOpen={isOpen} toggle={toggle} centered size="lg">
			<div className="modal-content">
				<ModalBody className="px-4 py-5">
					<h4 className="mb-4 text-center">Copiar Perfil por Empresa</h4>
					<FormGroup className="mb-3">
						<Label>Usuário origem</Label>
						<Select
							className="select2"
							styles={customStyles}
							placeholder="Selecione o usuário de origem"
							options={userOptions}
							value={userOptions.find(
								(option) => option.value === sourceUserId,
							)}
							onChange={(selected) => setSourceUserId(selected?.value)}
						/>
					</FormGroup>
					<FormGroup className="mb-4">
						<Label>Usuário destino</Label>
						<Select
							className="select2"
							styles={customStyles}
							placeholder="Selecione o usuário de destino"
							options={userOptions.filter(
								(option) => option.value !== sourceUserId,
							)}
							value={userOptions.find(
								(option) => option.value === targetUserId,
							)}
							onChange={(selected) => setTargetUserId(selected?.value)}
						/>
					</FormGroup>

					<div
						style={{
							border: "1px solid #ddd",
							borderRadius: 8,
							padding: 12,
							maxHeight: 300,
							overflowY: "auto",
						}}
					>
						<strong>Perfis por empresa que serao adicionados</strong>
						{!sourceUserId || !targetUserId ? (
							<div className="text-muted mt-2">
								Selecione origem e destino para visualizar os perfis.
							</div>
						) : profilesToAdd.length === 0 ? (
							<div className="text-muted mt-2">
								Nenhum novo vinculo para adicionar.
							</div>
						) : (
							<ul className="mt-2 mb-0 ps-3">
								{profilesToAdd.map((role) => (
									<li key={role.id || `${role.company_id}-${role.permission_id}`}>
										{role?.company?.name} - {role?.permission?.name}
									</li>
								))}
							</ul>
						)}
					</div>

					<div className="hstack gap-2 justify-content-end mt-4">
						<Button
							type="button"
							style={{
								background: "none",
								padding: 10,
								cursor: "pointer",
								color: "black",
							}}
							onClick={handleSave}
							disabled={!canSave || isSaving}
						>
							{isSaving ? "Salvando..." : "Salvar"}
						</Button>
						<Button
							type="button"
							onClick={toggle}
							style={{
								background: "none",
								padding: 10,
								cursor: "pointer",
								color: "black",
								display: "flex",
								alignItems: "center",
								gap: 2,
							}}
						>
							<SignOut size={16} /> Fechar
						</Button>
					</div>
				</ModalBody>
			</div>
		</Modal>
	);
};

export default CopyPerfilModal;
